import { React, useState, useEffect } from 'react';
import PageTitle from '../../common/components/PageTitle';
import { AvatarDisplay } from '../../common/components/AvatarDisplay';
import {
  Grid,
  Stack,
  Paper,
  Typography,
  Divider,
  Button,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/system';
import { selectSubscription } from '../../common/subscription/SubscriptionSlice';
import { useSelector } from 'react-redux';
import { formatDate } from '../../common/utils/formatFunctions';
import { PlanStatus } from './PlanStatus';
import PausedIcon from '@mui/icons-material/PauseCircleOutline';
import ResumeIcon from '@mui/icons-material/PlayCircleOutline';
import RepeatIcon from '@mui/icons-material/Repeat';
import CancelIcon from '@mui/icons-material/Cancel';
import ReactivateIcon from '@mui/icons-material/Replay';
import InfoIcon from '@mui/icons-material/Info';
import CouponIcon from '@mui/icons-material/Sell';
import PauseForm from './PauseForm';
import CancelPauseForm from './CancelPauseForm';
import CancelPendingChangeForm from './CancelPendingChangeForm';
import CancelSubscriptionForm from './CancelSubscriptionForm';
import ReactivateSubscriptionForm from './ReactivateSubscriptionForm';
import ResumeSubscriptionForm from './ResumeSubscriptionForm';
import ChangePlanForm from './ChangePlanForm';
import RedeemCouponForm from './RedeemCouponForm';
import { useNavigate } from 'react-router-dom';
import { SIGNUP } from '../../routes/CONSTANTS';
import {
  handleFetchAccountBalance,
  handleFetchRedeemedCoupons,
} from '../../common/subscription/SubscriptionService';

const Item = styled(Paper)(({ theme }) => ({
  boxShadow: 'none',
  borderRadius: 0,
  margin: theme.spacing(0.5),
}));

const LeftItem = styled(Paper)(({ theme }) => ({
  boxShadow: 'none',
  borderRadius: 0,
  margin: theme.spacing(0.5),
  width: '98%',
  textAlign: 'left',
}));

export function Subscription() {
  const subscriptionInfo = useSelector(selectSubscription);
  const [cancelingPendingPause, setCancelingPendingPause] = useState(false);
  const [cancelingPendingPlanChange, setCancelingPendingPlanChange] =
    useState(false);
  const [changingPlans, setChangingPlans] = useState(false);
  const [pausingSubscription, setPausingSubscription] = useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const [resumingSubscription, setResumingSubscription] = useState(false);
  const [reactivatingSubscription, setReactivatingSubscription] =
    useState(false);
  const [redeemingCoupon, setRedeemingCoupon] = useState(false);
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState();
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [loadingAccountBalance, setLoadingAccountBalance] = useState(false);
  const [balance, setBalance] = useState();

  const userHasSubscription =
    subscriptionInfo.SubscriptionID &&
    subscriptionInfo.SubscriptionState !== 'expired'
      ? true
      : false;
  const trialEndDateObj = new Date(subscriptionInfo.SubscriptionTrialEndsAt);
  const nowObj = new Date();
  const subscriptionInTrial = trialEndDateObj.getTime() > nowObj.getTime();

  //Decide what parts and buttons to show based on the subscription details
  const showNextPayment =
    userHasSubscription &&
    subscriptionInfo.SubscriptionState !== 'canceled' &&
    subscriptionInfo.SubscriptionPlanCode !== 'district';
  const showChangePlan =
    userHasSubscription &&
    subscriptionInfo.SubscriptionState !== 'expired' &&
    subscriptionInfo.SubscriptionState !== 'failed';
  const showPauseSubscription =
    userHasSubscription &&
    subscriptionInfo.SubscriptionState === 'active' &&
    !subscriptionInTrial;
  const showCancelSubscription =
    userHasSubscription &&
    subscriptionInfo.SubscriptionState !== 'canceled' &&
    subscriptionInfo.SubscriptionState !== 'expired' &&
    subscriptionInfo.SubscriptionState !== 'failed';
  const showResumeSubscription =
    userHasSubscription && subscriptionInfo.SubscriptionState === 'paused';
  const showReactivateSubscription =
    userHasSubscription && subscriptionInfo.SubscriptionState === 'canceled';
  const showDistrictTrialNote =
    subscriptionInTrial && subscriptionInfo.SubscriptionPlanCode === 'district';
  const showRedeemCoupon = true;

  useEffect(() => {
    setLoadingCoupons(true);
    handleFetchRedeemedCoupons()
      .then((res) => {
        setCoupons(res);
      })
      .catch((err) => {
        setCoupons();
      })
      .finally(() => {
        setLoadingCoupons(false);
      });
  }, []);

  useEffect(() => {
    setLoadingAccountBalance(true);
    handleFetchAccountBalance()
      .then((res) => {
        setBalance(res);
      })
      .catch((err) => {
        setBalance();
      })
      .finally(() => {
        setLoadingAccountBalance(false);
      });
  }, []);

  return (
    <>
      <PageTitle title='Subscription' />
      <Grid
        container
        alignItems='flex-start'
        justifyContent='center'
        direction='row'
      >
        <Grid item xs={12} md={6}>
          <AvatarDisplay />
        </Grid>
        <Grid item xs={12} md={6}>
          {subscriptionInfo.Loaded !== true ? (
            <>
              <Grid container spacing={10}>
                <Grid item xs={12}>
                  <CircularProgress color='secondary' />
                </Grid>
              </Grid>
            </>
          ) : cancelingPendingPause ? (
            <>
              <Stack alignItems='center' justifyContent='center'>
                <Item>
                  <Typography variant='h5'>Cancel Pending Pause</Typography>
                </Item>
                <Item sx={{ width: '50%' }}>
                  <Divider />
                </Item>
                <Item>
                  <CancelPauseForm
                    callback={() => setCancelingPendingPause(false)}
                  />
                </Item>
              </Stack>
            </>
          ) : changingPlans ? (
            <>
              <Stack alignItems='center' justifyContent='center'>
                <Item>
                  <Typography variant='h5'>Change Plan</Typography>
                </Item>
                <Item sx={{ width: '50%' }}>
                  <Divider />
                </Item>
                <Item>
                  <ChangePlanForm
                    callback={() => {
                      setChangingPlans(false);
                      setLoadingCoupons(true);
                      handleFetchRedeemedCoupons()
                        .then((res) => {
                          setCoupons(res);
                        })
                        .catch((err) => {
                          setCoupons();
                        })
                        .finally(() => {
                          setLoadingCoupons(false);
                        });
                    }}
                  />
                </Item>
              </Stack>
            </>
          ) : cancelingPendingPlanChange ? (
            <>
              <Stack alignItems='center' justifyContent='center'>
                <Item>
                  <Typography variant='h5'>
                    Cancel Pending Plan Change
                  </Typography>
                </Item>
                <Item sx={{ width: '50%' }}>
                  <Divider />
                </Item>
                <Item>
                  <CancelPendingChangeForm
                    callback={() => setCancelingPendingPlanChange(false)}
                  />
                </Item>
              </Stack>
            </>
          ) : pausingSubscription ? (
            <>
              <Stack alignItems='center' justifyContent='center'>
                <Item>
                  <Typography variant='h5'>Pause Subscription</Typography>
                </Item>
                <Item sx={{ width: '50%' }}>
                  <Divider />
                </Item>
                <Item>
                  <PauseForm callback={() => setPausingSubscription(false)} />
                </Item>
              </Stack>
            </>
          ) : cancelingSubscription ? (
            <>
              <Stack alignItems='center' justifyContent='center'>
                <Item>
                  <Typography variant='h5'>Cancel Subscription</Typography>
                </Item>
                <Item sx={{ width: '50%' }}>
                  <Divider />
                </Item>
                <Item>
                  <CancelSubscriptionForm
                    callback={() => setCancelingSubscription(false)}
                  />
                </Item>
              </Stack>
            </>
          ) : resumingSubscription ? (
            <>
              <Stack alignItems='center' justifyContent='center'>
                <Item>
                  <Typography variant='h5'>Resume Subscription</Typography>
                </Item>
                <Item sx={{ width: '50%' }}>
                  <Divider />
                </Item>
                <Item>
                  <ResumeSubscriptionForm
                    callback={() => setResumingSubscription(false)}
                  />
                </Item>
              </Stack>
            </>
          ) : reactivatingSubscription ? (
            <>
              <Stack alignItems='center' justifyContent='center'>
                <Item>
                  <Typography variant='h5'>Reactivate Subscription</Typography>
                </Item>
                <Item sx={{ width: '50%' }}>
                  <Divider />
                </Item>
                <Item>
                  <ReactivateSubscriptionForm
                    callback={() => setReactivatingSubscription(false)}
                  />
                </Item>
              </Stack>
            </>
          ) : redeemingCoupon ? (
            <>
              <Stack alignItems='center' justifyContent='center'>
                <Item>
                  <Typography variant='h5'>Redeem Coupon</Typography>
                </Item>
                <Item sx={{ width: '50%' }}>
                  <Divider />
                </Item>
                <Item>
                  <RedeemCouponForm
                    callback={() => {
                      setRedeemingCoupon(false);
                      setLoadingCoupons(true);
                      handleFetchRedeemedCoupons()
                        .then((res) => {
                          setCoupons(res);
                        })
                        .catch((err) => {
                          setCoupons();
                        })
                        .finally(() => {
                          setLoadingCoupons(false);
                        });
                    }}
                  />
                </Item>
              </Stack>
            </>
          ) : userHasSubscription ? (
            <Stack alignItems='center' justifyContent='center'>
              <Item>
                <Typography variant='h5'>Subscription Information</Typography>
              </Item>
              <Item sx={{ width: '50%' }}>
                <Divider />
              </Item>
              {showDistrictTrialNote && (
                <LeftItem>
                  <Typography>
                    <InfoIcon
                      sx={{ color: 'primary.main' }}
                      style={{ verticalAlign: 'middle' }}
                    />{' '}
                    NOTE: District plans start on a 1-day free trial prior to
                    the sign up code being processed and the full year
                    membership being activated.
                  </Typography>
                </LeftItem>
              )}
              <LeftItem>
                <PlanStatus
                  status={
                    subscriptionInTrial
                      ? 'active trial'
                      : subscriptionInfo.SubscriptionState
                  }
                />
              </LeftItem>
              <LeftItem>
                <Typography>
                  <b>
                    {subscriptionInTrial
                      ? 'Trial Ends'
                      : subscriptionInfo.SubscriptionState === 'canceled'
                      ? 'Subscription Expires'
                      : 'Current Period Ends'}
                  </b>
                  <br />
                  {formatDate(subscriptionInfo.SubscriptionCurrentPeriodEndsAt)}
                </Typography>
              </LeftItem>
              <LeftItem>
                <Typography>
                  <b>Plan: {subscriptionInfo.SubscriptionPlanName}</b> <br />
                  {subscriptionInfo.PlanDescription}
                </Typography>
              </LeftItem>
              {subscriptionInfo.SubscriptionRemainingPauseCycles > 0 &&
              subscriptionInfo.SubscriptionState === 'active' ? (
                <>
                  <LeftItem>
                    <Typography>
                      <b>Pending Pause</b>
                      <br />
                      <PausedIcon
                        sx={{ color: 'warning.main' }}
                        style={{ verticalAlign: 'middle' }}
                      />{' '}
                      Your subscription will be paused on{' '}
                      {formatDate(subscriptionInfo.SubscriptionPausedAt)} for{' '}
                      {subscriptionInfo.SubscriptionRemainingPauseCycles}{' '}
                      billing cylces.
                    </Typography>
                  </LeftItem>
                  <Item>
                    <Button
                      variant='contained'
                      color='secondary'
                      onClick={() => setCancelingPendingPause(true)}
                    >
                      <CancelIcon style={{ marginRight: '5' }} />
                      Cancel Pending Pause
                    </Button>
                  </Item>
                </>
              ) : (
                subscriptionInfo.SubscriptionState === 'paused' && (
                  <LeftItem>
                    <Typography>
                      <b>Remaining Pause Cycles</b>
                      <br />
                      {subscriptionInfo.SubscriptionRemainingPauseCycles ===
                      0 ? (
                        'There are 0 remaining pause cycles, your subscription will automaticaly resume at the current period end date'
                      ) : (
                        <>
                          Your subscription is paused for{' '}
                          {subscriptionInfo.SubscriptionRemainingPauseCycles}{' '}
                          more billing cycle
                          {subscriptionInfo.SubscriptionRemainingPauseCycles >
                            1 && 's'}
                          .
                        </>
                      )}
                    </Typography>
                  </LeftItem>
                )
              )}
              {subscriptionInfo.PendingChangeId && (
                <>
                  <LeftItem>
                    <Typography>
                      <b>Pending Plan Change</b>
                      <br />
                      <RepeatIcon
                        sx={{ color: 'success.main' }}
                        style={{ verticalAlign: 'middle' }}
                      />{' '}
                      Your subscription will convert to the{' '}
                      {subscriptionInfo.PendingChangeNewPlanName} plan on{' '}
                      {formatDate(subscriptionInfo.PendingChangeEffectiveDate)}.
                    </Typography>
                  </LeftItem>
                  <Item>
                    <Button
                      variant='contained'
                      color='secondary'
                      onClick={() => setCancelingPendingPlanChange(true)}
                    >
                      <CancelIcon style={{ marginRight: '5' }} />
                      Cancel Pending Plan Change{' '}
                    </Button>
                  </Item>
                </>
              )}
              {showNextPayment && (
                <LeftItem>
                  <Typography>
                    <b>Next Payment</b>
                    <br />
                    {subscriptionInfo.SubscriptionCurrency === 'USD' ? '$' : ''}
                    {subscriptionInfo.NextChargeAmount.toFixed(2)}{' '}
                    {subscriptionInfo.SubscriptionCurrency} on:{' '}
                    {formatDate(subscriptionInfo.NextChargeDate)} for plan:{' '}
                    {subscriptionInfo.NextChargePlanName}{' '}
                    {'(does not reflect coupons or discounts)'}
                  </Typography>
                </LeftItem>
              )}
              {loadingCoupons ? (
                <>
                  <CircularProgress color='secondary' />
                </>
              ) : (
                coupons &&
                Array.isArray(coupons) && (
                  <LeftItem>
                    <Typography>
                      <b>Active Coupons</b>
                      <br />
                      {coupons.map((coupon) => (
                        <>
                          <CouponIcon style={{ verticalAlign: 'middle' }} />
                          <i>{coupon.CouponCode}</i>: {coupon.CouponDescription}
                          <br />
                        </>
                      ))}
                    </Typography>
                  </LeftItem>
                )
              )}
              {loadingAccountBalance ? (
                <>
                  <CircularProgress color='secondary' />
                </>
              ) : balance && balance.AccountPastDue ? (
                <LeftItem>
                  <Typography>
                    <b>Account Balance</b>
                    <br />
                    We were unable to process your previous payment(s) and you
                    have a balance of{' '}
                    {balance.AccountBalanceCurrency === 'USD' ? '$' : ''}
                    {balance.AccountPastDueBalance}{' '}
                    {balance.AccountBalanceCurrency} that is past due. Please
                    verify that your billing information is still valid and
                    contact us if you have further questions.
                  </Typography>
                </LeftItem>
              ) : (
                balance &&
                balance.AccountCreditBalance !== 0 && (
                  <LeftItem>
                    <Typography>
                      <b>Account Credit</b>
                      <br />
                      There is a credit of{' '}
                      {balance.AccountBalanceCurrency === 'USD' ? '$' : ''}
                      {balance.AccountCreditBalance * -1}{' '}
                      {balance.AccountBalanceCurrency}* on your account. This
                      will be automaticaly applied to future invoices.
                    </Typography>
                    <Typography variant='subtitle2'>
                      *This amount is available to be applied to future invoices
                      on this account only, it not transferrable, redeemable as
                      cash, or as a refund for previous payments.
                    </Typography>
                  </LeftItem>
                )
              )}
              {showChangePlan && (
                <Item>
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={() => setChangingPlans(true)}
                  >
                    <RepeatIcon style={{ marginRight: '5' }} />
                    Change Plan
                  </Button>
                </Item>
              )}
              {showPauseSubscription && (
                <Item>
                  <Button
                    variant='contained'
                    color='secondary'
                    onClick={() => setPausingSubscription(true)}
                  >
                    <PausedIcon style={{ marginRight: '5' }} />
                    Pause Subscription
                  </Button>
                </Item>
              )}
              {showResumeSubscription && (
                <Item>
                  <Button
                    variant='contained'
                    color='secondary'
                    onClick={() => setResumingSubscription(true)}
                  >
                    <ResumeIcon style={{ marginRight: '5' }} />
                    Resume Subscription
                  </Button>
                </Item>
              )}
              {showReactivateSubscription && (
                <Item>
                  <Button
                    variant='contained'
                    color='secondary'
                    onClick={() => setReactivatingSubscription(true)}
                  >
                    <ReactivateIcon style={{ marginRight: '5' }} />
                    Reactivate Subscription
                  </Button>
                </Item>
              )}
              {showCancelSubscription && (
                <Item>
                  <Button
                    variant='contained'
                    color='tertiary'
                    onClick={() => setCancelingSubscription(true)}
                  >
                    <CancelIcon style={{ marginRight: '5' }} />
                    Cancel Subscription
                  </Button>
                </Item>
              )}
              {showRedeemCoupon && (
                <Item>
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={() => setRedeemingCoupon(true)}
                  >
                    <CouponIcon style={{ marginRight: '5' }} />
                    Redeem Coupon
                  </Button>
                </Item>
              )}
            </Stack>
          ) : (
            <>
              <Stack alignItems='center' justifyContent='center'>
                <Item>
                  <Typography variant='h5'>Subscription Information</Typography>
                </Item>
                <Item sx={{ width: '50%' }}>
                  <Divider />
                </Item>
                <LeftItem>
                  <PlanStatus status='expired' />
                </LeftItem>
                <LeftItem>
                  <Typography>
                    You do not currently have a subscription to LINKtivity®
                    Learning or access to the library.
                  </Typography>
                </LeftItem>
                <Item>
                  <Button
                    variant='contained'
                    color='secondary'
                    onClick={() => navigate(SIGNUP)}
                  >
                    <ResumeIcon style={{ marginRight: '5' }} />
                    Start Subscription
                  </Button>
                </Item>
              </Stack>
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
}
